import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { AGE_GATES, CONSENT_VERSIONS } from '@erectus/shared';
import { Button, Card, ErrorState, SectionTitle, Toggle } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { canOptInToDataSharing, useAuth } from '@/stores/auth.js';
import { saveConsent } from '@/lib/repo.js';
import { track, setProps } from '@/lib/analytics.js';
import { SharingBenefits } from './SharingBenefits.js';
import { sendSessionsOnPhone } from './sendSessionsOnPhone.js';

/**
 * "Help improve detection" opt-in (docs/DATA_AND_ML.md §1, LEGAL §3).
 *
 * Deliberate wording choices:
 *  - 18+ only, gated on the birth year already on file.
 *  - Consent is a tap on a clearly worded choice, never a pre-set default
 *    (MHMDA "clear affirmative act"). The same question is asked at sign-up.
 *    The tap is recorded with the notice version and time; that electronic
 *    record is the written release BIPA-style statutes ask for. It replaced
 *    a typed-name signature, which added friction without adding consent.
 *  - The words "research" and "study" appear nowhere: this is product
 *    improvement, and using research framing would drag the app into the app
 *    stores' human-subjects rules (LEGAL §0).
 */
export function DataOptInPage(): ReactNode {
  const { user, userDoc, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const current = userDoc?.consent;
  const [agree, setAgree] = useState(current?.dataOptIn ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eligible = canOptInToDataSharing(userDoc?.profile?.birthYear);

  if (!eligible) {
    return (
      <div className="space-y-4">
        <PageHeader title="Help improve detection" />
        <ErrorState
          title="Not available"
          message={`Sharing keypoint data is limited to people aged ${AGE_GATES.dataOptInAge} and over.`}
          action={
            <Link className="text-sm font-semibold underline" to="/settings">
              Back to settings
            </Link>
          }
        />
      </div>
    );
  }

  const save = async (optIn: boolean): Promise<void> => {
    if (!user || !current) return;
    setBusy(true);
    setError(null);
    try {
      await saveConsent(user.uid, {
        ...current,
        dataOptIn: optIn,
        dataOptInAt: optIn ? Date.now() : current.dataOptInAt,
        dataOptInVersion: optIn ? CONSENT_VERSIONS.dataOptIn : current.dataOptInVersion,
        dataOptInSignature: null,
        dataWithdrawnAt: optIn ? null : Date.now(),
      });
      track('data_optin', { value: optIn });
      setProps({ has_data_optin: optIn ? 'true' : 'false' });
      // After the consent is saved: the storage rule reads it.
      if (optIn && !current.dataOptIn) void sendSessionsOnPhone(user.uid);
      await refreshUserDoc();
      navigate('/settings', { replace: true });
    } catch {
      setError('Could not save your choice. Check your connection.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Help improve detection"
        subtitle="Optional. Measuring and reports work the same either way."
      />

      {error ? <ErrorState title="Not saved" message={error} /> : null}

      <Card className="space-y-3">
        <SectionTitle>What you would share</SectionTitle>
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
          <li>The joint coordinates from your sessions (numbers, not pictures) — from now on, and the sessions still on this phone (the last 7 days) when you turn this on</li>
          <li>The stretches you mark as wrong, and the reason you pick</li>
        </ul>
        <SectionTitle>What is never shared</SectionTitle>
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-soft">
          <li>Video or images — they never leave your device in the first place</li>
          <li>Your name or email: shared files carry a separate scrambled ID</li>
        </ul>
        <SectionTitle>How it is used</SectionTitle>
        <p className="text-sm text-ink-soft">
          We use it only to make the posture detection in this app better — for example, teaching it to ignore
          the moments when you reach for a cup instead of calling it a slouch.
        </p>
        <SectionTitle>What it does for you</SectionTitle>
        <SharingBenefits />
        <SectionTitle>Withdrawing</SectionTitle>
        <p className="text-sm text-ink-soft">
          Turn this off any time in Settings. New sessions stop being shared immediately, and
          what you already shared is deleted. Replays then go back to the last 7 days on this phone.
        </p>
      </Card>

      <Card className="space-y-3">
        <Toggle
          checked={agree}
          onChange={setAgree}
          label="Yes, share my keypoint data to improve detection"
          description={`Version ${CONSENT_VERSIONS.dataOptIn}. Ages ${AGE_GATES.dataOptInAge}+.`}
        />
        <div className="flex gap-2">
          <Button
            className="flex-1"
            disabled={busy}
            onClick={() => void save(agree)}
          >
            {busy ? 'Saving…' : agree ? 'Turn on sharing' : 'Keep sharing off'}
          </Button>
        </div>
        {current?.dataOptIn ? (
          <Button variant="danger" disabled={busy} onClick={() => void save(false)}>
            Withdraw and stop sharing
          </Button>
        ) : null}
      </Card>

      <p className="text-xs text-muted">
        See the{' '}
        <Link className="underline" to="/legal/detection-improvement">
          detection-improvement data notice
        </Link>{' '}
        and the{' '}
        <Link className="underline" to="/legal/data-retention">
          retention policy
        </Link>
        .
      </p>
    </div>
  );
}
